import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuthStore } from '../stores';
import { User2, Wallet, Save, RefreshCw, AlertCircle, Check, Edit } from 'lucide-react';
import API_BASE_URL from '../config/api';

interface User {
  id: string;
  username: string;
  role: 'admin' | 'user';
  save_history: 0 | 1;
  balance: number;
  created_at: string;
}

const UsersManagementPage: React.FC = () => {
  const { user: currentUser, token } = useAuthStore();
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editBalance, setEditBalance] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const fetchUsers = async () => {
    if (!token) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Ошибка загрузки пользователей');
      }
      
      const result = await response.json();
      setUsers(result.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Неизвестная ошибка');
      console.error('Ошибка загрузки пользователей:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser?.role === 'admin') {
      fetchUsers();
    }
  }, [token, currentUser]);

  const handleEditBalance = (userId: string, currentBalance: number) => {
    setEditingUserId(userId);
    setEditBalance(currentBalance.toFixed(2));
    setSuccessMessage(null);
  };

  const handleSaveBalance = async (userId: string) => {
    if (!token) return;
    
    const newBalance = parseFloat(editBalance);
    if (isNaN(newBalance)) {
      setError('Неверный формат суммы');
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/balance`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ balance: newBalance })
      });
      
      if (!response.ok) {
        throw new Error('Ошибка обновления баланса');
      }
      
      // Обновляем локальный список пользователей
      setUsers(users.map(u => 
        u.id === userId ? { ...u, balance: newBalance } : u
      ));
      
      setEditingUserId(null);
      setSuccessMessage(`Баланс пользователя обновлён: ${newBalance.toFixed(2)} ₽`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Неизвестная ошибка');
      console.error('Ошибка сохранения баланса:', err);
    }
  };

  const handleCancelEdit = () => {
    setEditingUserId(null);
    setEditBalance('');
  };

  if (currentUser?.role !== 'admin') {
    return (
      <Container>
        <ErrorMessage>
          <AlertCircle size={20} />
          У вас нет прав доступа к этой странице
        </ErrorMessage>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>
          <User2 size={28} />
          Управление пользователями
        </Title>
        <RefreshButton onClick={fetchUsers} disabled={isLoading}>
          <RefreshCw size={18} />
          Обновить
        </RefreshButton>
      </Header>

      {error && (
        <ErrorMessage>
          <AlertCircle size={20} />
          {error}
        </ErrorMessage>
      )}

      {successMessage && (
        <SuccessMessage>
          <Check size={20} />
          {successMessage}
        </SuccessMessage>
      )}

      {isLoading ? (
        <LoadingMessage>Загрузка пользователей...</LoadingMessage>
      ) : users.length === 0 ? (
        <EmptyMessage>Нет зарегистрированных пользователей</EmptyMessage>
      ) : (
        <TableWrapper>
          <StyledTable>
            <thead>
              <tr>
                <Th>Имя пользователя</Th>
                <Th>Роль</Th>
                <Th>История</Th>
                <Th>Баланс</Th>
                <Th>Дата регистрации</Th>
                <Th>Действия</Th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id}>
                  <Td>
                    <Username>{user.username}</Username>
                  </Td>
                  <Td>
                    <RoleBadge $isAdmin={user.role === 'admin'}>
                      {user.role === 'admin' ? 'Администратор' : 'Пользователь'}
                    </RoleBadge>
                  </Td>
                  <Td>
                    <HistoryStatus $enabled={user.save_history === 1}>
                      {user.save_history === 1 ? '✓ Включено' : '✗ Отключено'}
                    </HistoryStatus>
                  </Td>
                  <Td>
                    {editingUserId === user.id ? (
                      <BalanceEdit>
                        <BalanceInput
                          type="number"
                          step="0.01"
                          value={editBalance}
                          onChange={(e) => setEditBalance(e.target.value)}
                          autoFocus
                        />
                        <span>₽</span>
                      </BalanceEdit>
                    ) : (
                      <Balance $positive={user.balance > 0} $negative={user.balance < 0}>
                        <Wallet size={16} />
                        {user.balance.toFixed(2)} ₽
                      </Balance>
                    )}
                  </Td>
                  <Td>
                    <DateTime>
                      {new Date(user.created_at).toLocaleString('ru-RU', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </DateTime>
                  </Td>
                  <Td>
                    {editingUserId === user.id ? (
                      <ActionButtons>
                        <SaveButton onClick={() => handleSaveBalance(user.id)}>
                          <Save size={16} />
                          Сохранить
                        </SaveButton>
                        <CancelButton onClick={handleCancelEdit}>
                          Отмена
                        </CancelButton>
                      </ActionButtons>
                    ) : (
                      <EditButton onClick={() => handleEditBalance(user.id, user.balance)}>
                        <Edit size={16} />
                        Изменить баланс
                      </EditButton>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </StyledTable>
        </TableWrapper>
      )}
    </Container>
  );
};

export default UsersManagementPage;

// Styled Components
const Container = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0;
`;

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #0284c7;
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #0369a1;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(2, 132, 199, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: #fee;
  color: #c33;
  border-radius: 0.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid #fcc;
`;

const SuccessMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: #efe;
  color: #3a3;
  border-radius: 0.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid #cfc;
  animation: fadeIn 0.3s;

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: #6b7280;
  font-size: 1.1rem;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: #6b7280;
  font-size: 1.1rem;
`;

const TableWrapper = styled.div`
  background: white;
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  padding: 1.25rem 1rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: white;
  background: linear-gradient(135deg, #0284c7 0%, #0369a1 100%);
  border-bottom: 2px solid #0369a1;
  white-space: nowrap;

  &:first-child {
    border-top-left-radius: 1rem;
  }

  &:last-child {
    border-top-right-radius: 1rem;
  }
`;

const Td = styled.td`
  padding: 1rem;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
  vertical-align: middle;

  tr:last-child & {
    border-bottom: none;
  }

  tr:hover & {
    background: #f9fafb;
  }
`;

const Username = styled.div`
  font-weight: 600;
  font-size: 1rem;
  color: #111827;
`;

const RoleBadge = styled.span<{ $isAdmin: boolean }>`
  display: inline-block;
  padding: 0.35rem 0.75rem;
  background: ${props => props.$isAdmin ? '#3b82f6' : '#6b7280'};
  color: white;
  border-radius: 0.375rem;
  font-size: 0.85rem;
  font-weight: 500;
`;

const HistoryStatus = styled.span<{ $enabled: boolean }>`
  color: ${props => props.$enabled ? '#10b981' : '#6b7280'};
  font-weight: 500;
  font-size: 0.95rem;
`;

const Balance = styled.div<{ $positive?: boolean; $negative?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  font-size: 1.05rem;
  color: ${props => 
    props.$positive ? '#10b981' : 
    props.$negative ? '#ef4444' : 
    '#6b7280'};
`;

const BalanceEdit = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const BalanceInput = styled.input`
  width: 120px;
  padding: 0.5rem;
  border: 2px solid #0284c7;
  border-radius: 0.375rem;
  font-size: 1rem;
  font-weight: 600;
  outline: none;

  &:focus {
    border-color: #0369a1;
    box-shadow: 0 0 0 3px rgba(2, 132, 199, 0.1);
  }
`;

const DateTime = styled.div`
  color: #6b7280;
  font-size: 0.9rem;
  white-space: nowrap;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const EditButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #0284c7;
  color: white;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: #0369a1;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(2, 132, 199, 0.3);
  }
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: #059669;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
  }
`;

const CancelButton = styled.button`
  padding: 0.5rem 1rem;
  background: transparent;
  color: #6b7280;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
    color: #374151;
  }
`;
